import { FC } from "react"
import { proxy, useSnapshot } from "valtio"
import { visuallyHidden } from "@mui/utils"

/**
 * Shared state for screen reader announcements.
 * Update `AnnouncerState.message` from anywhere to trigger an announcement.
 */
export const AnnouncerState = proxy({ message: "" })

/**
 * Announce a message to screen readers.
 * Clears and re-sets the message so repeated identical values still trigger.
 */
export const announce = (message: string) => {
  AnnouncerState.message = ""
  requestAnimationFrame(() => {
    AnnouncerState.message = message
  })
}

/**
 * A persistent assertive live region for screen reader announcements.
 * Render this above any widget roles (e.g. role="radio") that suppress
 * nested aria-live regions — such as in AssessmentItem.
 */
export const ScreenReaderAnnouncer: FC = () => {
  const { message } = useSnapshot(AnnouncerState)
  return (
    <div
      role="status"
      aria-live="assertive"
      aria-atomic="true"
      style={visuallyHidden}
    >
      {message}
    </div>
  )
}
